import React, { CSSProperties, FC, useCallback, useEffect, useRef, useState } from 'react';

interface SafeColorInputProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  style?: CSSProperties;
}

/**
 * A safe color input component that uses native HTML elements to avoid
 * Mantine ColorInput ref callback issues with React 18+.
 *
 * Features:
 * - Visual color picker with native <input type="color">
 * - Text input for manual hex value entry
 * - Bidirectional sync between both inputs
 * - Hex color validation
 * - Accessible with proper labels and titles
 */
export const SafeColorInput: FC<SafeColorInputProps> = ({
  value,
  onChange,
  label,
  placeholder = '#000000',
  disabled = false,
  className,
  style,
}) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const [isPickerActive, setIsPickerActive] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Only sync from parent if user is not actively interacting
    if (!isFocused && !isPickerActive) {
      setLocalValue((prev) => (prev !== value ? value : prev));
    }
  }, [value, isFocused, isPickerActive]);

  // Debounced onChange to prevent rapid updates during color picker drag
  const debouncedOnChange = useCallback(
    (newValue: string) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        onChange(newValue);
        setIsPickerActive(false);
      }, 100);
    },
    [onChange],
  );

  const isHex6 = (v: string): boolean => /^#[0-9A-Fa-f]{6}$/.test(v);
  const isHex3 = (v: string): boolean => /^#[0-9A-Fa-f]{3}$/.test(v);
  const ensureHash = (v: string): string => (v && !v.startsWith('#') ? `#${v}` : v);
  const expandHex3 = (v: string): string => {
    const hex = v.replace('#', '');
    if (hex.length !== 3) return v;
    const [a, b, c] = hex.split('');
    return `#${a}${a}${b}${b}${c}${c}`;
  };

  const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setIsPickerActive(true);
    setLocalValue(newValue);
    debouncedOnChange(newValue);
  };

  const handleTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    // Allow empty string or valid hex colors (with or without #)
    if (newValue === '' || /^#?[0-9A-Fa-f]{0,6}$/.test(newValue)) {
      // Ensure hex values start with #
      const formattedValue = newValue && !newValue.startsWith('#') ? `#${newValue}` : newValue;
      setLocalValue(formattedValue);

      // Only call onChange for complete hex colors or empty string
      if (formattedValue === '' || /^#[0-9A-Fa-f]{6}$/.test(formattedValue)) {
        onChange(formattedValue);
      }
    }
  };

  const handleTextInputBlur = () => {
    setIsFocused(false);
    if (!localValue || localValue.trim() === '') {
      // Revert to last committed value from parent
      setLocalValue(value || '#000000');
      return;
    }

    // Normalize and commit on blur
    let v = ensureHash(localValue.trim());
    if (isHex3(v)) {
      v = expandHex3(v);
    }

    if (isHex6(v)) {
      setLocalValue(v);
      onChange(v);
    } else {
      // Invalid input -> revert to last committed value
      setLocalValue(value || '#000000');
    }
  };

  const handleTextInputFocus = () => setIsFocused(true);

  const pickerValue = (() => {
    const v = ensureHash(localValue || '');
    if (isHex6(v)) return v;
    if (isHex3(v)) return expandHex3(v);
    return '#000000';
  })();

  const containerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '120px',
    ...style,
  };

  const colorPickerStyle: CSSProperties = {
    width: '30px',
    height: '30px',
    borderRadius: '16px',
    border: '1px solid #ced4da',
    cursor: disabled ? 'not-allowed' : 'pointer',
    padding: '0',
    backgroundColor: 'transparent',
    opacity: disabled ? 0.6 : 1,
  };

  const textInputStyle: CSSProperties = {
    flexGrow: 1,
    height: '36px',
    borderRadius: '16px',
    border: '1px solid #ced4da',
    padding: '0 12px',
    fontSize: '14px',
    fontFamily: 'inherit',
    backgroundColor: disabled ? '#f8f9fa' : 'white',
    cursor: disabled ? 'not-allowed' : 'text',
    opacity: disabled ? 0.6 : 1,
  };

  return (
    <div className={className} style={containerStyle}>
      <input
        type="color"
        value={pickerValue}
        onChange={handleColorPickerChange}
        disabled={disabled}
        style={colorPickerStyle}
        title={`Select color for ${label}`}
        aria-label={`Color picker for ${label}`}
      />
      <input
        type="text"
        value={localValue}
        onChange={handleTextInputChange}
        onFocus={handleTextInputFocus}
        onBlur={handleTextInputBlur}
        disabled={disabled}
        placeholder={placeholder}
        style={textInputStyle}
        title={`Hex color value for ${label}`}
        aria-label={`Hex color input for ${label}`}
        maxLength={7} // #FFFFFF
      />
    </div>
  );
};
